import type {
  SaldoCicloCardProps,
  SaldoCicloMetricaSecundaria,
  SaldoCicloState,
} from '@/shared/components/ui/common/SaldoCicloCard';

import type {
  ActiveContractByCategory,
  ActiveContractCurrentPeriod,
  BuildSaldoCicloViewOptions,
} from './types';
import { franquiaDeMensagemEsgotada, franquiaEsgotada } from './utils/franquiaCiclo';
import { saldoBaixo, saldoDeMensagemBaixo, testeAcabando } from './utils/limiaresPlano';
import { formatarDiaMes } from './utils/periodoContrato';

function comDiaMes(prefixo: string, iso: string | null | undefined): string | undefined {
  const diaMes = iso ? formatarDiaMes(iso) : '';
  return diaMes ? `${prefixo} ${diaMes}` : undefined;
}

function resolveInfoLabel(data: ActiveContractByCategory): string | undefined {
  if (data.status === 'TRIAL') {
    return comDiaMes('teste expira em', data.trialEnd);
  }

  return comDiaMes('renova em', data.currentPeriod?.periodEnd);
}

function resolveInfoLabelSemPlano(
  data: ActiveContractByCategory | null | undefined,
): string | undefined {
  if (!data?.trialEnd || data.status !== 'EXPIRED') return undefined;
  return comDiaMes('seu teste terminou em', data.trialEnd);
}

function resolveState(data: ActiveContractByCategory): SaldoCicloState {
  const total = data.currentPeriod?.totalBalance ?? null;

  if (data.status === 'TRIAL' && total === null) return 'ilimitado';
  if (data.status === 'TRIAL') return 'trial';
  if (total === null) return 'semFranquia';
  if (franquiaEsgotada(data.currentPeriod)) return 'esgotado';
  if (saldoBaixo(data.currentPeriod)) return 'atencao';
  return 'normal';
}

export function buildSaldoCicloView(
  data: ActiveContractByCategory | null | undefined,
  options?: BuildSaldoCicloViewOptions,
): SaldoCicloCardProps {
  if (options?.loading) {
    return { productName: data?.productName ?? 'Plano', state: 'loading' };
  }

  if (options?.error) {
    return { productName: data?.productName ?? 'Plano', state: 'erro' };
  }

  if (!data || !data.hasActive || data.status === 'EXPIRED' || data.status === 'CANCELED') {
    return {
      productName: data?.productName ?? 'Plano',
      state: 'semPlano',
      infoLabel: resolveInfoLabelSemPlano(data),
      onRenovar: options?.onRenovar,
    };
  }

  return {
    productName: data.productName ?? 'Plano',
    planLabel: data.planLabel ?? undefined,
    state: resolveState(data),
    used: data.currentPeriod?.usedBalance ?? 0,
    total: data.currentPeriod?.totalBalance ?? null,
    primaryLabel: 'Disparos',
    secondaryMetric: buildMetricaDeMensagem(data),
    infoLabel: resolveInfoLabel(data),
    paymentMode: data.paymentMode,
    trialEndingSoon: testeAcabando(data),
    onRenovar: options?.onRenovar,
  };
}

function buildMetricaDeMensagem(
  data: ActiveContractByCategory,
): SaldoCicloMetricaSecundaria | undefined {
  const periodo = data.currentPeriod;
  if (!periodo || periodo.messageBalance == null) return undefined;

  return {
    label: 'Mensagens',
    used: periodo.messageUsed,
    total: periodo.messageBalance,
    state: resolveStateDeMensagem(periodo),
  };
}

function resolveStateDeMensagem(periodo: ActiveContractCurrentPeriod): SaldoCicloState {
  if (franquiaDeMensagemEsgotada(periodo)) return 'esgotado';
  if (saldoDeMensagemBaixo(periodo)) return 'atencao';
  return 'normal';
}

const SEM_PERCENTUAL: SaldoCicloState[] = ['ilimitado', 'semFranquia', 'semPlano'];

function percentualDeUso(saldo: SaldoCicloCardProps): number {
  if (SEM_PERCENTUAL.includes(saldo.state)) return -1;
  const total = saldo.total ?? 0;
  if (total <= 0) return -1;
  return (saldo.used ?? 0) / total;
}

const ESTADOS_PENDENTES: SaldoCicloState[] = ['loading', 'erro'];

const ehPendente = (saldo: SaldoCicloCardProps): boolean => ESTADOS_PENDENTES.includes(saldo.state);

export function pickSidebarSaldos(
  list: SaldoCicloCardProps[],
  max = 1,
): { visible: SaldoCicloCardProps[]; overflow: number } {
  const contratados = list.filter((saldo) => saldo.state !== 'semPlano' && !ehPendente(saldo));

  if (contratados.length === 0) {
    const reserva = list.find(ehPendente) ?? list.find((saldo) => saldo.state === 'semPlano');
    return { visible: reserva ? [reserva] : [], overflow: 0 };
  }

  const sorted = [...contratados].sort((a, b) => percentualDeUso(b) - percentualDeUso(a));
  return { visible: sorted.slice(0, max), overflow: Math.max(0, sorted.length - max) };
}
